import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiCheckBold,
    PiXBold,
    PiUploadSimpleBold,
    PiReceiptBold,
    PiCalendarBold,
    PiUserBold,
    PiArrowCounterClockwiseBold,
} from 'react-icons/pi';
import { Link } from 'react-router-dom';
import { PageHeader, LoadingState } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import api from '@v2/lib/api';
import type { CommissionMonthlyNf } from './types';

type PendingAction =
    | { type: 'mark_paid'; nf: CommissionMonthlyNf }
    | { type: 'cancel'; nf: CommissionMonthlyNf }
    | { type: 'consolidate_bulk'; period: string };

const STATUS_BADGE: Record<string, string> = {
    open:          'bg-gray-100 text-gray-700',
    awaiting_nf:   'bg-yellow-100 text-yellow-700',
    nf_submitted:  'bg-blue-100 text-blue-700',
    paid:          'bg-green-100 text-green-700',
    cancelled:     'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<string, string> = {
    open:         'Em aberto',
    awaiting_nf:  'Aguardando NF',
    nf_submitted: 'NF Enviada',
    paid:         'Pago',
    cancelled:    'Cancelado',
};

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function modalCopy(action: PendingAction) {
    switch (action.type) {
        case 'mark_paid':
            return {
                title: 'Confirmar pagamento da NF mensal?',
                description: `NF mensal de ${action.nf.user_name} — ${action.nf.period_label} (${fmt(action.nf.total_amount)}). Todos os registros do mês serão marcados como pagos.`,
                confirmLabel: 'Confirmar pagamento',
            };
        case 'cancel':
            return {
                title: 'Cancelar NF mensal?',
                description: `Os registros vinculados voltarão para "Aguardando NF" individualmente.`,
                confirmLabel: 'Cancelar NF mensal',
            };
        case 'consolidate_bulk':
            return {
                title: `Gerar NFs mensais — ${action.period}?`,
                description: `Todos os registros pendentes de NF do período ${action.period} serão agrupados em NFs mensais por vendedor. Um único envio de NF por vendedor.`,
                confirmLabel: 'Gerar NFs mensais',
            };
    }
}

export default function CommissionMonthlyNfsPage() {
    const qc = useQueryClient();
    const uploadRef = useRef<HTMLInputElement>(null);
    const [uploadingId, setUploadingId] = useState<number | null>(null);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [bulkPeriod, setBulkPeriod] = useState(currentPeriod());
    const [filters, setFilters] = useState({
        status: '',
        period: currentPeriod(),
        per_page: 25,
        page: 1,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['commissioning', 'monthly-nfs', filters],
        queryFn: () => api.get('/commissioning/monthly-nfs', { params: filters }).then((r) => r.data),
        placeholderData: (prev) => prev,
    });

    const nfs: CommissionMonthlyNf[] = data?.data ?? [];
    const meta = data?.meta;

    // ── Consolidar em lote ────────────────────────────────────────────────────
    const consolidateBulkMutation = useMutation({
        mutationFn: (period: string) =>
            api.post('/commissioning/monthly-nfs/consolidate-bulk', { period }).then((r) => r.data),
        onSuccess: (res) => {
            toast.success(res.message ?? 'NFs mensais geradas!');
            setPendingAction(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'monthly-nfs'] });
        },
        onError: () => toast.error('Erro ao gerar NFs mensais.'),
    });

    // ── Upload de NF ─────────────────────────────────────────────────────────
    const uploadMutation = useMutation({
        mutationFn: async ({ id, file }: { id: number; file: File }) => {
            const form = new FormData();
            form.append('invoice', file);
            return api.post(`/commissioning/monthly-nfs/${id}/submit-nf`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            toast.success('NF enviada com sucesso!');
            setUploadingId(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'monthly-nfs'] });
        },
        onError: () => toast.error('Erro ao enviar NF. Verifique o arquivo (PDF ou XML).'),
    });

    // ── Marcar pago ──────────────────────────────────────────────────────────
    const markPaidMutation = useMutation({
        mutationFn: (id: number) => api.post(`/commissioning/monthly-nfs/${id}/mark-paid`),
        onSuccess: () => {
            toast.success('NF mensal marcada como paga!');
            setPendingAction(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'monthly-nfs'] });
        },
        onError: () => toast.error('Erro ao marcar como pago.'),
    });

    // ── Cancelar ─────────────────────────────────────────────────────────────
    const cancelMutation = useMutation({
        mutationFn: (id: number) => api.post(`/commissioning/monthly-nfs/${id}/cancel`),
        onSuccess: () => {
            toast.success('NF mensal cancelada. Registros liberados.');
            setPendingAction(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'monthly-nfs'] });
        },
        onError: () => toast.error('Erro ao cancelar.'),
    });

    const confirmLoading =
        (pendingAction?.type === 'consolidate_bulk' && consolidateBulkMutation.isPending)
        || (pendingAction?.type === 'mark_paid' && markPaidMutation.isPending)
        || (pendingAction?.type === 'cancel' && cancelMutation.isPending);

    function handleConfirmAction() {
        if (!pendingAction) return;
        switch (pendingAction.type) {
            case 'consolidate_bulk':
                consolidateBulkMutation.mutate(pendingAction.period);
                break;
            case 'mark_paid':
                markPaidMutation.mutate(pendingAction.nf.id);
                break;
            case 'cancel':
                cancelMutation.mutate(pendingAction.nf.id);
                break;
        }
    }

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>, nf: CommissionMonthlyNf) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingId(nf.id);
        uploadMutation.mutate({ id: nf.id, file });
        e.target.value = '';
    }

    const modal = pendingAction ? modalCopy(pendingAction) : null;

    return (
        <div className="space-y-6">
            <PageHeader
                title="NFs Mensais"
                subtitle="Uma nota fiscal por vendedor por mês — comissões + salário"
            >
                <div className="flex flex-wrap gap-2 items-center">
                    {/* Gerar NFs mensais em lote */}
                    <div className="flex items-center gap-1">
                        <input
                            type="month"
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={bulkPeriod}
                            onChange={(e) => setBulkPeriod(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setPendingAction({ type: 'consolidate_bulk', period: bulkPeriod })}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                            disabled={consolidateBulkMutation.isPending}
                        >
                            <PiReceiptBold className="w-4 h-4" />
                            Gerar NFs do mês
                        </button>
                    </div>
                    <Link
                        to="/commissioning/records"
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Ver registros detalhados
                    </Link>
                </div>
            </PageHeader>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3">
                <select
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.status}
                    onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value, page: 1 }))}
                >
                    <option value="">Todos os status</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                <input
                    type="month"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.period}
                    onChange={(e) => setFilters((p) => ({ ...p, period: e.target.value, page: 1 }))}
                    placeholder="Período"
                />
                {meta && (
                    <span className="text-sm text-gray-500 self-center ml-auto">
                        {meta.total} NF(s) mensal(is)
                    </span>
                )}
            </div>

            {isLoading && <LoadingState variant="table" />}

            {/* Input oculto para upload */}
            <input
                ref={uploadRef}
                type="file"
                accept=".pdf,.xml"
                className="hidden"
                onChange={(e) => {
                    const nf = nfs.find((n) => n.id === uploadingId);
                    if (nf) handleFileChange(e, nf);
                }}
            />

            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium">
                                <span className="flex items-center gap-1">
                                    <PiCalendarBold className="w-3.5 h-3.5" /> Período
                                </span>
                            </th>
                            <th className="px-4 py-3 text-left font-medium">
                                <span className="flex items-center gap-1">
                                    <PiUserBold className="w-3.5 h-3.5" /> Vendedor
                                </span>
                            </th>
                            <th className="px-4 py-3 text-right font-medium">Comissões</th>
                            <th className="px-4 py-3 text-right font-medium">Salário</th>
                            <th className="px-4 py-3 text-right font-medium">Total NF</th>
                            <th className="px-4 py-3 text-left font-medium">Status</th>
                            <th className="px-4 py-3 text-left font-medium">Prazo</th>
                            <th className="px-4 py-3 text-center font-medium">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {nfs.map((nf) => (
                            <tr key={nf.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                    <p className="font-semibold text-gray-900">{nf.period_label}</p>
                                    <p className="text-xs text-gray-400">{nf.records_count} parcela(s)</p>
                                </td>
                                <td className="px-4 py-3 font-medium">{nf.user_name}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{fmt(nf.total_commissions)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{fmt(nf.total_salary)}</td>
                                <td className="px-4 py-3 text-right font-bold text-gray-900">{fmt(nf.total_amount)}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[nf.status] ?? 'bg-gray-100 text-gray-700'}`}>
                                        {nf.status_label}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">
                                    {nf.status === 'awaiting_nf' && nf.days_until_deadline != null ? (
                                        <span className={nf.days_until_deadline <= 3 ? 'text-red-600 font-medium' : ''}>
                                            {nf.days_until_deadline} dia(s)
                                            {nf.nf_deadline_at && (
                                                <span className="block text-gray-400">
                                                    até {nf.nf_deadline_at.split(' ')[0]}
                                                </span>
                                            )}
                                        </span>
                                    ) : '—'}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1">
                                        {/* Ver detalhe */}
                                        <Link
                                            to={`/commissioning/monthly-nfs/${nf.id}`}
                                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title="Ver detalhe"
                                        >
                                            <PiReceiptBold className="w-4 h-4" />
                                        </Link>

                                        {/* Enviar NF (awaiting_nf) */}
                                        {nf.status === 'awaiting_nf' && (
                                            <button
                                                type="button"
                                                disabled={uploadMutation.isPending && uploadingId === nf.id}
                                                onClick={() => {
                                                    setUploadingId(nf.id);
                                                    uploadRef.current?.click();
                                                }}
                                                className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
                                                title="Enviar NF do mês"
                                            >
                                                <PiUploadSimpleBold className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Marcar pago (nf_submitted) */}
                                        {nf.status === 'nf_submitted' && (
                                            <button
                                                type="button"
                                                onClick={() => setPendingAction({ type: 'mark_paid', nf })}
                                                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                title="Confirmar pagamento"
                                            >
                                                <PiCheckBold className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Cancelar */}
                                        {!['paid', 'cancelled'].includes(nf.status) && (
                                            <button
                                                type="button"
                                                onClick={() => setPendingAction({ type: 'cancel', nf })}
                                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Cancelar NF mensal"
                                            >
                                                <PiXBold className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Link para download da NF enviada */}
                                        {nf.invoice_url && (
                                            <a
                                                href={nf.invoice_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                                title="Baixar NF enviada"
                                            >
                                                <PiArrowCounterClockwiseBold className="w-4 h-4 rotate-180" />
                                            </a>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {nfs.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-gray-500 text-sm">
                                    Nenhuma NF mensal encontrada.
                                    {filters.status === '' && (
                                        <p className="mt-1 text-xs">
                                            Use "Gerar NFs do mês" para consolidar os registros de comissão.
                                        </p>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {meta && (
                <TablePagination
                    meta={meta}
                    onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
                    onPerPageChange={(per_page) => setFilters((f) => ({ ...f, per_page, page: 1 }))}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="NF mensal"
                />
            )}

            {modal && (
                <ConfirmModal
                    open={pendingAction !== null}
                    onClose={() => !confirmLoading && setPendingAction(null)}
                    onConfirm={handleConfirmAction}
                    title={modal.title}
                    description={modal.description}
                    confirmLabel={modal.confirmLabel}
                    loading={confirmLoading}
                />
            )}
        </div>
    );
}
